import type { ReactNode } from 'react'
import type { Host, InstallInfo, NodeInfo } from '../../api'
import { agentState } from '../Hosts'
import { releasesBehind } from '../provisioning'
import { ago, clock } from './format'
import { DRAIN_SPEC, NODE_SPEC, RELAY_STATE_SPEC, nodeIssues, nodeView, peersText } from './states'
import { StateBadge } from './ui'

/*
 * Column "Status" of Network › Nodes: the node state (glyph + word) and, under
 * it, three icons without text — host agent, peers list, clock. The icons are
 * always there (grey when fine), so a 5 s update never moves the row; a problem
 * adds a corner mark (▲ / ✕ / ─) besides the colour, so the state reads without
 * colour. The details are in the tooltip and in aria-label.
 */

type Tone = 'ok' | 'warn' | 'fail' | 'off'

const TONE: Record<Tone, string> = {
  ok: 'border-line-2 text-muted',
  warn: 'border-warn/50 text-warn',
  fail: 'border-fail/50 text-fail',
  off: 'border-dashed border-line-2 text-faint',
}
const MARK: Record<Tone, string> = { ok: '', warn: '▲', fail: '✕', off: '─' }

const PICTO: Record<'agent' | 'peers' | 'clock', ReactNode> = {
  // a host: two stacked units with a lamp each
  agent: (
    <>
      <rect x="2" y="2.5" width="10" height="4" rx=".8" />
      <rect x="2" y="7.5" width="10" height="4" rx=".8" />
      <path d="M4 4.5h.01M4 9.5h.01" strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),
  // the peers list: three nodes that trust each other
  peers: (
    <>
      <circle cx="7" cy="3" r="1.6" />
      <circle cx="3" cy="10.5" r="1.6" />
      <circle cx="11" cy="10.5" r="1.6" />
      <path d="M6.2 4.4 3.8 9.1M7.8 4.4l2.4 4.7M4.6 10.5h4.8" />
    </>
  ),
  clock: (
    <>
      <circle cx="7" cy="7" r="5" />
      <path d="M7 4v3l2 1.5" strokeLinecap="round" />
    </>
  ),
}

function Ind({ kind, tone, label, testId, data }: { kind: keyof typeof PICTO; tone: Tone; label: string; testId?: string; data?: Record<string, string | number | boolean | undefined> }) {
  const attrs = Object.fromEntries(Object.entries(data ?? {}).map(([k, v]) => [`data-${k}`, v === undefined ? undefined : String(v)]))
  return (
    <span
      role="img"
      tabIndex={0}
      aria-label={label}
      title={label}
      data-testid={testId}
      data-tone={tone}
      {...attrs}
      className={`relative inline-grid h-6 w-6 flex-none place-items-center rounded-sm border focus-visible:border-accent focus-visible:shadow-focus focus-visible:outline-none ${TONE[tone]}`}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden>
        {PICTO[kind]}
      </svg>
      {MARK[tone] && (
        <span aria-hidden className="absolute -top-1.5 -right-1.5 grid h-3 min-w-3 place-items-center rounded-full bg-bg font-mono text-[9px] leading-none">
          {MARK[tone]}
        </span>
      )}
    </span>
  )
}

/** Host agent (M2.4): undefined host = provisioning off (no icon); null = installed by hand. */
function AgentInd({ n, host, info }: { n: NodeInfo; host: Host | null; info?: InstallInfo }) {
  if (!host) return <Ind kind="agent" tone="off" label="Host agent: none, the node was installed by hand" testId={`node-host-${n.id}`} data={{ agent: 'manual' }} />
  const a = agentState(host)
  const behind = releasesBehind(host, info)
  const lag = behind > 0 ? ` · ${behind} release${behind === 1 ? '' : 's'} behind` : ''
  const tone: Tone = a.key === 'online' ? (behind > 0 ? 'warn' : 'ok') : a.key === 'installing' ? 'warn' : 'fail'
  return <Ind kind="agent" tone={tone} label={`Host agent: ${a.text.replace(/^Agent /, '')}${lag}`} testId={`node-host-${n.id}`} data={{ agent: a.key, behind }} />
}

/** Peers list of the relay (R0.9): in it and trusted by the others. */
function PeersInd({ n }: { n: NodeInfo }) {
  const r = n.relay
  if (!r) return <Ind kind="peers" tone="off" label="Peers list: the node reports no relay" testId={`node-in-peers-${n.id}`} data={{ state: 'NO RELAY' }} />
  const p = peersText(r)
  const off = r.state === 'disabled' || r.state === 'unsupported' || r.state === 'revoked'
  const counted = !off && r.peers_total !== undefined && r.peers_acked !== undefined
  const partial = counted && (r.peers_total ?? 0) > 0 && (r.peers_acked ?? 0) < (r.peers_total ?? 0)
  const tone: Tone = r.peers_rejected ? 'fail' : off ? 'off' : !r.in_peers || partial ? 'warn' : 'ok'
  const parts = [
    off ? RELAY_STATE_SPEC[r.state].word.toLowerCase() : r.in_peers ? `in peers, v${r.peers_version_acked}` : r.state === 'unregistered' ? 'no relay key yet' : `pending, v${r.peers_version_acked} of v${r.peers_version}`,
    counted ? ((r.peers_total ?? 0) === 0 ? 'only node' : `trusted by ${r.peers_acked} of ${r.peers_total}`) : '',
    r.peers_rejected ? `refused v${r.peers_rejected.version}: ${r.peers_rejected.reason} (${clock(r.peers_rejected.at)})` : '',
  ].filter(Boolean)
  return (
    <Ind
      kind="peers"
      tone={tone}
      label={`Peers list: ${parts.join(' · ')}`}
      testId={`node-in-peers-${n.id}`}
      data={{ state: p.spec.word, acked: counted ? r.peers_acked : undefined, total: counted ? r.peers_total : undefined }}
    />
  )
}

/** Node clock minus the center's. */
function ClockInd({ n }: { n: NodeInfo }) {
  const c = n.clock
  if (n.status !== 'online' || !c) return <Ind kind="clock" tone="off" label={n.status === 'online' ? 'Clock: no report' : 'Clock: no report while the node is off line'} testId={`node-clock-${n.id}`} />
  const off = `${c.offset_ms > 0 ? '+' : ''}${Math.abs(c.offset_ms) < 10 ? c.offset_ms.toFixed(1) : Math.round(c.offset_ms)} ms`
  const bad = c.suspect || c.synced === false
  const word = c.synced === false ? 'not synced' : c.suspect ? 'suspect' : c.synced ? 'synced' : c.source
  return <Ind kind="clock" tone={bad ? 'warn' : 'ok'} label={`Clock: ${off} vs the center · ${word}`} testId={`node-clock-${n.id}`} data={{ suspect: c.suspect, synced: c.synced }} />
}

/** Status of the M2 lifecycle in words, under the state badge: rotation and the last report. */
/** The drain of the node (MOVE-1): the badge and what is left, until when. */
export function DrainLine({ n }: { n: NodeInfo }) {
  const d = n.drain
  if (!d || n.status === 'revoked') return null
  const left =
    d.reported && d.state === 'draining'
      ? [`${d.publishers ?? 0} streams`, `${d.outputs ?? 0} outputs`, ...(d.legs ? [`${d.legs} legs`] : [])].join(' · ')
      : ''
  return (
    <span className="flex flex-col items-start gap-1" data-testid={`node-drain-${n.id}`} data-state={d.state}>
      <StateBadge spec={DRAIN_SPEC[d.state]} />
      <span className="text-[11px] leading-snug text-muted">
        {d.state === 'draining' && d.deadline ? `until ${clock(d.deadline)}` : ''}
        {left ? `${d.state === 'draining' && d.deadline ? ' · ' : ''}${left}` : ''}
        {!d.reported ? `${d.deadline ? ' · ' : ''}ordered, not reported yet` : ''}
        {d.moved ? ` · ${d.moved} moved out` : ''}
      </span>
    </span>
  )
}

export function Lifecycle({ n, table }: { n: NodeInfo; table?: boolean }) {
  if (n.status === 'revoked') return <span className="block text-[11px] text-muted">revoked {ago(n.revoked_at)}</span>
  if (n.status === 'offline') return <span className="block text-[11px] text-fail">last report {ago(n.last_seen_at)}</span>
  if (!n.accept_ingest)
    return (
      <span className="block text-[11px] text-warn" data-testid={table ? `node-rotation-${n.id}` : undefined} title="Not offered for new streams; running broadcasts continue">
        Out of rotation
      </span>
    )
  return null
}

/**
 * The table cell. `host` undefined = provisioning off (no agent icon), null = a
 * node installed by hand; `relay` false = the center has no relay data (no peers icon).
 */
export function StatusCell({ n, host, info, relay }: { n: NodeInfo; host?: Host | null; info?: InstallInfo; relay: boolean }) {
  const v = nodeView(n)
  const issues = nodeIssues(n)
  return (
    <span className="flex min-w-0 flex-col items-start gap-1.5" data-testid={`node-status-${n.id}`} data-view={v}>
      <StateBadge spec={NODE_SPEC[v]} />
      {v === 'degraded' && (
        <span className="text-[11px] leading-snug text-warn" data-testid={`node-issues-${n.id}`}>
          {issues.join(' · ')}
        </span>
      )}
      <DrainLine n={n} />
      <Lifecycle n={n} table />
      {n.status !== 'revoked' && (
        <span className="flex items-center gap-2 pt-0.5" data-testid={`node-indicators-${n.id}`}>
          {host !== undefined && <AgentInd n={n} host={host} info={info} />}
          {relay && <PeersInd n={n} />}
          <ClockInd n={n} />
        </span>
      )}
    </span>
  )
}

/** Gear of the row: opens and closes the node's settings under it. */
export function GearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.5v1.8M8 12.7v1.8M1.5 8h1.8M12.7 8h1.8M3.4 3.4l1.3 1.3M11.3 11.3l1.3 1.3M3.4 12.6l1.3-1.3M11.3 4.7l1.3-1.3" strokeLinecap="round" />
      <circle cx="8" cy="8" r="4.6" />
    </svg>
  )
}
